import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format cost values with appropriate unit (EUR, kEUR, MEUR)
 * - Small values (<1,000): Show as EUR (e.g., "€500")
 * - Medium values (>=1,000 and <1,000,000): Show as kEUR (e.g., "€5.2k")
 * - Large values (>=1,000,000): Show as MEUR (e.g., "€1.5M")
 */
export function formatCost(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '€0'

  if (value >= 1000000) {
    return `€${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `€${(value / 1000).toFixed(1)}k`
  }
  return `€${Math.round(value).toLocaleString()}`
}

/**
 * Format cost with separate value and unit for KPI card display
 * Returns { value: "2.0", unit: "M€" } format
 */
export function formatCostWithUnit(value: number | null | undefined): { value: string; unit: string } {
  if (value === null || value === undefined || Number.isNaN(value)) return { value: '0', unit: '€' }

  if (value >= 1000000) {
    return { value: (value / 1000000).toFixed(1), unit: 'M€' }
  }
  if (value >= 1000) {
    return { value: (value / 1000).toFixed(1), unit: 'k€' }
  }
  return { value: Math.round(value).toLocaleString(), unit: '€' }
}

/**
 * Format CO2 values in tons (t CO2) or kilotonnes (kt CO2)
 * Input is assumed to be in kg, converts to tons
 * Returns an object with formatted value and unit
 */
export function formatCO2Tons(kgValue: number | null | undefined): string {
  if (kgValue === null || kgValue === undefined) return '0'
  const tons = kgValue / 1000
  if (tons >= 1000) {
    return (tons / 1000).toFixed(1)
  }
  if (tons >= 10) {
    return Math.round(tons).toLocaleString()
  }
  return tons.toFixed(1)
}

/**
 * Get the appropriate CO2 unit based on the value
 * Returns 'kt' for kilotonnes (>=1000 tons) or 't' for tons
 */
export function getCO2Unit(kgValue: number | null | undefined): string {
  if (kgValue === null || kgValue === undefined) return 't'
  const tons = kgValue / 1000
  return tons >= 1000 ? 'kt' : 't'
}

/**
 * Format square meters as integer (no decimals)
 */
export function formatSqm(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0'
  return Math.round(value).toLocaleString()
}

/**
 * Format energy with appropriate unit (kWh, MWh, GWh)
 * Returns an object with formatted value and unit for flexible display
 * - Small values (<1,000 kWh): Show as kWh
 * - Medium values (1,000-10,000 kWh): Show as MWh with 1 decimal
 * - Large values (10,000-1,000,000 kWh): Show as MWh (no decimal)
 * - Very large values (>=1,000,000 kWh): Show as GWh
 */
export function formatEnergyWithUnit(value: number | null | undefined): { value: string; unit: string } {
  if (value === null || value === undefined) return { value: '-', unit: '' }
  const absValue = Math.abs(value)
  if (absValue >= 1000000) {
    // GWh for very large values (>= 1 million kWh)
    return { value: (value / 1000000).toFixed(1), unit: 'GWh' }
  }
  if (absValue >= 10000) {
    // MWh for annual building consumption (>10,000 kWh) - no decimal
    return { value: (value / 1000).toFixed(0), unit: 'MWh' }
  }
  if (absValue >= 1000) {
    // MWh with decimal for medium values
    return { value: (value / 1000).toFixed(1), unit: 'MWh' }
  }
  return { value: Math.round(value).toLocaleString(), unit: 'kWh' }
}

/**
 * Format energy as a simple string with unit (e.g., "223 GWh")
 * Convenience wrapper around formatEnergyWithUnit
 */
export function formatEnergy(value: number | null | undefined): string {
  const { value: v, unit } = formatEnergyWithUnit(value)
  if (v === '-') return '-'
  return `${v} ${unit}`
}

export type EnergyUnit = 'kWh' | 'MWh' | 'GWh'

/**
 * Determine the appropriate energy unit for a group of values
 * Based on the maximum absolute value in the group
 */
export function getConsistentEnergyUnit(values: (number | null | undefined)[]): EnergyUnit {
  const validValues = values.filter((v): v is number => v !== null && v !== undefined)
  if (validValues.length === 0) return 'kWh'

  const maxAbsValue = Math.max(...validValues.map(v => Math.abs(v)))

  if (maxAbsValue >= 1000000) return 'GWh'
  if (maxAbsValue >= 1000) return 'MWh'
  return 'kWh'
}

// ============================================================================
// Figures — THE display format for numbers the COO chat shows (blocks, tables,
// chart axes and tooltips). The app's display convention: "." is the decimal
// point (formatEnergy, DeltaPill, KPICards) and thousands are grouped with a
// narrow no-break space, so "41 235" can never be misread as 41.235.
// ============================================================================

/** Thousands separator of every figure: U+202F NARROW NO-BREAK SPACE. */
export const FIGURE_GROUP = ' '

const ENERGY_TO_KWH: Record<string, number> = { kWh: 1, MWh: 1_000, GWh: 1_000_000 }

/** The unit and divisor one set of figures (a column, an axis) is shown in. */
export interface FigureScale {
  unit: string
  divisor: number
}

const FIGURE_NUMBER = [0, 1].map(
  (digits) => new Intl.NumberFormat('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }),
)
// Axis ticks: as many decimals as the tick needs (0.35, 0.7, 1), never a rounded-off tick.
const TICK_NUMBER = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 })

function groupDigits(value: number, digits: 0 | 1): string {
  return FIGURE_NUMBER[digits]!.format(value).replace(/,/g, FIGURE_GROUP)
}

/** An axis tick in a figure scale: same separators and unit scale as formatFigure, exact decimals. */
export function formatFigureTick(value: number, unit?: string, scale?: FigureScale): string {
  if (unit === '%') return formatFigure(value, unit)
  const s = scale ?? figureScale([value], unit)
  return TICK_NUMBER.format(value / s.divisor).replace(/,/g, FIGURE_GROUP)
}

/**
 * ONE unit for a set of values: energy (kWh, MWh, GWh) is normalised to kWh and
 * scaled by the largest magnitude (getConsistentEnergyUnit); any other unit is
 * shown as given.
 */
export function figureScale(values: Array<number | null | undefined>, unit?: string): FigureScale {
  const factor = unit ? ENERGY_TO_KWH[unit] : undefined
  if (factor === undefined) return { unit: unit ?? '', divisor: 1 }
  const kwh = values.map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v * factor : null))
  const shown = getConsistentEnergyUnit(kwh)
  return { unit: shown, divisor: ENERGY_TO_KWH[shown]! / factor }
}

/** A figure split into its number and its (possibly scaled) unit, e.g. { value: '41.2', unit: 'MWh' }. */
export function formatFigureParts(
  value: number | null | undefined,
  unit?: string,
  scale?: FigureScale,
): { value: string; unit: string } {
  if (value === null || value === undefined || Number.isNaN(value)) return { value: '—', unit: '' }
  if (unit === '%') {
    const sign = value > 0 ? '+' : value < 0 ? '-' : ''
    return { value: `${sign}${groupDigits(Math.abs(value), 1)}%`, unit: '' }
  }
  const s = scale ?? figureScale([value], unit)
  const scaled = value / s.divisor
  const whole = Number.isInteger(scaled) || Math.abs(scaled) >= 100
  return { value: groupDigits(scaled, whole ? 0 : 1), unit: s.unit }
}

/** "41.2 MWh", "+12.3%", "41 235", "—". Pass `scale` so a column or axis stays in one unit. */
export function formatFigure(value: number | null | undefined, unit?: string, scale?: FigureScale): string {
  const f = formatFigureParts(value, unit, scale)
  return f.unit ? `${f.value} ${f.unit}` : f.value
}

/**
 * Format energy value using a specific unit (for consistent display across siblings)
 */
/** EnPI unit label — single source of truth for all displays */
export const ENPI_UNIT = 'kWh/m²/y'


export function formatEnergyWithFixedUnit(
  value: number | null | undefined,
  unit: EnergyUnit
): string {
  if (value === null || value === undefined) return '-'

  switch (unit) {
    case 'GWh':
      return (value / 1000000).toFixed(1)
    case 'MWh':
      // Use decimal for smaller MWh values, no decimal for larger
      const mwhValue = value / 1000
      return Math.abs(mwhValue) >= 10 ? mwhValue.toFixed(0) : mwhValue.toFixed(1)
    case 'kWh':
    default:
      return Math.round(value).toLocaleString()
  }
}

// ============================================================================
// Danish (da-DK) locale formatters — used by bulk PDF invoice review (SEN-228)
// where DKK is the only currency and Danish customers expect "70.070 kWh" /
// "DKK 12.430" / "1,04" formatting regardless of the browser's Accept-Language
// header. Pinning the locale to 'da-DK' makes the rendered values
// deterministic across browsers and CI screenshots.
// ============================================================================

/**
 * Format a number with Danish locale rules (`.` thousands, `,` decimal).
 * Returns '—' for null/undefined/NaN so the caller never has to special-case
 * empty cells. Use this when you need a raw numeric string without a unit
 * (e.g. inside an input element where the unit is rendered separately).
 */
export function formatNumberDk(
  value: number | null | undefined,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  const { minimumFractionDigits = 0, maximumFractionDigits = 0 } = options
  return value.toLocaleString('da-DK', {
    minimumFractionDigits,
    maximumFractionDigits,
  })
}

/**
 * THE canonical parser for a number a user types or pastes into a text
 * input (consumption kWh, rates). Danish-first. Every text input that writes
 * consumption values goes through this; `parseClipboardNumbers` calls it per
 * cell. Returns NaN for anything it cannot read unambiguously, so the caller
 * decides how to recover. (`<input type="number">` values are already
 * canonical `1234.5` strings from the browser and must NOT go through here.)
 *
 * Whitespace is accepted only as 3-digit thousands grouping (`70 070`,
 * `1 234,5`; `1000 2000` → NaN). Then, after an optional leading sign:
 *
 *  1. Digits only → integer. `70070` → 70070.
 *  2. Comma only → the comma is the decimal separator. `1,5` → 1.5,
 *     `1,234` → 1.234 (never 1234). More than one comma → NaN.
 *  3. Dot only:
 *     - several dots → Danish thousands grouping; every group after the
 *       first must be exactly 3 digits. `1.234.567` → 1234567.
 *     - ONE dot followed by EXACTLY 3 digits, after a 1–3 digit head that
 *       is not `0` → thousands. `70.070` → 70070, `2.000` → 2000.
 *     - any other single dot → English decimal. `12.5` → 12.5,
 *       `0.500` → 0.5, `1234.567` → 1234.567.
 *  4. Both `.` and `,` → whichever comes LAST is the decimal separator; the
 *     other is grouping (3-digit groups, else NaN).
 *     `1.234,5` → 1234.5, `1,234.5` → 1234.5.
 *  Anything else (letters, `1.23.4`, `1,2,3`) → NaN.
 *
 * Why rule 3: kWh is rendered da-DK (`70.070`), so re-typing what is on
 * screen must round-trip; a dot followed by 1, 2 or 4+ digits can only be a
 * decimal point. The residual ambiguity (`1.500` meant as 1.5) resolves to
 * Danish grouping, which is the convention of the app.
 */
export function parseNumberDk(input: string): number {
  if (input == null) return NaN
  const trimmed = String(input).trim()
  if (!trimmed) return NaN
  // Whitespace (incl. NBSP / narrow NBSP from locale-formatted copies) is
  // only accepted as thousands grouping: `70 070`, `1 234,5`. `1000 2000`
  // is two numbers glued together, not 10002000 → NaN.
  if (/\s/.test(trimmed) && !/^[-+]?\d{1,3}(\s\d{3})+([.,]\d+)?$/.test(trimmed)) {
    return NaN
  }
  let s = trimmed.replace(/\s+/g, '')

  let sign = 1
  if (s[0] === '-' || s[0] === '+') {
    if (s[0] === '-') sign = -1
    s = s.slice(1)
  }
  // Strict: digits and separators only. `12abc` is NaN, not 12.
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return NaN

  const lastDot = s.lastIndexOf('.')
  const lastComma = s.lastIndexOf(',')
  let intPart: string
  let fracPart = ''
  let groupSep: '.' | ',' | null = null

  if (lastDot >= 0 && lastComma >= 0) {
    // Rule 4: both present → the LAST one is the decimal separator.
    const decSep = lastComma > lastDot ? ',' : '.'
    groupSep = decSep === ',' ? '.' : ','
    const decIdx = s.lastIndexOf(decSep)
    intPart = s.slice(0, decIdx)
    fracPart = s.slice(decIdx + 1)
    if (intPart.includes(decSep) || fracPart.includes(groupSep)) return NaN
  } else if (lastComma >= 0) {
    // Rule 2: comma only → always the decimal separator.
    const parts = s.split(',')
    if (parts.length !== 2) return NaN
    intPart = parts[0] ?? ''
    fracPart = parts[1] ?? ''
  } else if (lastDot >= 0) {
    // Rule 3: dot only.
    const parts = s.split('.')
    const head = parts[0] ?? ''
    const isGrouping =
      parts.length > 2 ||
      (/^\d{3}$/.test(parts[1] ?? '') && /^\d{1,3}$/.test(head) && head !== '0')
    if (isGrouping) {
      groupSep = '.'
      intPart = s
    } else {
      intPart = head
      fracPart = parts[1] ?? ''
    }
  } else {
    // Rule 1: digits only.
    intPart = s
  }

  if (groupSep !== null && intPart.includes(groupSep)) {
    const groups = intPart.split(groupSep)
    const [first, ...rest] = groups
    if (!/^\d{1,3}$/.test(first ?? '') || !rest.every((g) => /^\d{3}$/.test(g))) {
      return NaN
    }
    intPart = groups.join('')
  }
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart)) return NaN
  if (intPart === '' && fracPart === '') return NaN

  const n = Number(`${intPart || '0'}.${fracPart || '0'}`)
  return Number.isFinite(n) ? sign * n : NaN
}

/**
 * Format a kWh value with Danish locale ("70.070 kWh"). No unit conversion —
 * unlike formatEnergy this always renders kWh because the bulk PDF grid
 * shows raw invoice values, not portfolio aggregates.
 */
export function formatKwhDk(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return `${formatNumberDk(value, { maximumFractionDigits: 0 })} kWh`
}

/**
 * Format a DKK monetary value with Danish locale ("DKK 12.430").
 * `decimals` defaults to 0 because invoice totals are typically whole DKK.
 */
export function formatDkk(
  value: number | null | undefined,
  decimals: number = 0,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  const formatted = formatNumberDk(value, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  return `DKK ${formatted}`
}

/**
 * Format a DKK/kWh rate with Danish locale ("1,04"). Always 2 decimals to
 * match utility rate sheets; never appends a unit because the rate cell
 * renders the unit ("DKK/kWh") separately so it can be styled independently.
 */
export function formatRateDk(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return formatNumberDk(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
